import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetFolderDto } from '../../../models/image/dto/media-manager-asset-folder-dto';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { applyUpdatedAssets } from './asset-patch';
import { findAssetInLibrary, findFolderInLibrary } from './asset-library-lookups';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

/** Move a folder to a new parent in the filesystem. */
export function moveFolder(
  folderId: string,
  targetParentFolderId: string,
  filesystem: MediaManagerFileSystem
): boolean {
  return filesystem.moveFolder(folderId, targetParentFolderId);
}

/** Patch folder metadata (name, timestamps, etc.) in the filesystem. */
export function patchFolderMetadata(
  folderId: string,
  updatedFolder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem
): boolean {
  return filesystem.patchFolderMetadata(folderId, updatedFolder);
}

/**
 * Copy server-owned mutable fields from an updated folder onto the canonical asset library entry in place. The
 * asset library is mutated by reference; keeping this list in one place makes drift easier to spot.
 */
export function patchAssetLibraryFolder(
  currentFolder: MediaManagerFolderDto,
  updatedFolder: MediaManagerFolderDto
): void {
  currentFolder.companyId = updatedFolder.companyId;
  currentFolder.dateCreated = updatedFolder.dateCreated;
  currentFolder.lastModified = updatedFolder.lastModified;
  currentFolder.name = updatedFolder.name;
  currentFolder.parentFolderId = updatedFolder.parentFolderId;
}

/**
 * Reconcile the filesystem and asset library entries for a single folder update. Returns false if the identifier
 * changes or any filesystem mutation refuses to apply so the caller can fall back to a refetch.
 */
export function applyUpdatedFolder(
  currentFolder: MediaManagerFolderDto,
  updatedFolder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem
): boolean {
  const currentFolderId = currentFolder.id;
  if (currentFolderId !== updatedFolder.id) {
    return false;
  }
  const parentChanged = currentFolder.parentFolderId !== updatedFolder.parentFolderId;
  const folderNameChanged = currentFolder.name !== updatedFolder.name;
  if (parentChanged && !moveFolder(currentFolderId, updatedFolder.parentFolderId, filesystem)) {
    return false;
  }
  if (folderNameChanged && !patchFolderMetadata(currentFolderId, updatedFolder, filesystem)) {
    return false;
  }
  patchAssetLibraryFolder(currentFolder, updatedFolder);
  return true;
}

/** Batch counterpart of {@link applyUpdatedFolder}. Bails at the first failed lookup or mutation. */
export function applyUpdatedFolders(
  currentFoldersById: Map<string, MediaManagerFolderDto>,
  updatedFolders: MediaManagerFolderDto[],
  filesystem: MediaManagerFileSystem
): boolean {
  for (const updatedFolder of updatedFolders) {
    const currentFolder = currentFoldersById.get(updatedFolder.id);
    if (!currentFolder) return false;
    if (!applyUpdatedFolder(currentFolder, updatedFolder, filesystem)) return false;
  }
  return true;
}

/**
 * Insert a newly created folder into both the filesystem and the asset library. Returns false when the folder
 * already exists (duplicate reconciliation) or when the filesystem refuses to add it.
 */
export function applyCreatedFolder(
  createdFolder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  if (!createdFolder?.id?.trim()) {
    return false;
  }
  if (findFolderInLibrary(createdFolder.id, assetLibrary)) {
    return false;
  }
  if (!filesystem.createFolder(createdFolder)) {
    return false;
  }
  assetLibrary.assetFolders.push(createdFolder);
  return true;
}

/** Build a mutated copy of a child folder pointing at a new parent, preserving all other fields. */
export function buildUpdatedFolder(
  currentChildFolder: MediaManagerFolderDto,
  targetFolderId: string
): MediaManagerFolderDto {
  const updatedFolder = window?.injector?.Deserialize?.instanceOf(MediaManagerFolderDto, currentChildFolder);
  updatedFolder.parentFolderId = targetFolderId;
  return updatedFolder;
}

/** Build a mutated copy of a child asset pointing at a new parent, preserving all other fields. */
export function buildUpdatedAsset(currentChildAsset: Asset, targetFolderId: string): Asset {
  const updatedAsset = window?.injector?.Deserialize?.instanceOf(Asset, currentChildAsset);
  updatedAsset.parentFolderId = targetFolderId;
  return updatedAsset;
}

/** Bulk-remove a folder subtree from the asset library after the filesystem has already dropped it. */
export function removeDeletedFolderSubtreeFromAssetLibrary(
  deletedFolderIds: Set<string>,
  deletedAssetIds: Set<string>,
  assetLibrary: MediaManagerAssetLibraryDto
): void {
  assetLibrary.assetFolders = assetLibrary.assetFolders.filter(folder => !deletedFolderIds.has(folder.id));
  assetLibrary.assets = assetLibrary.assets.filter(asset => !deletedAssetIds.has(asset.getUniqueIdentifier()));
}

/**
 * Move the direct children of a deleted folder into a target folder before the parent is removed. When the
 * server response includes updated child entries they are preferred over the locally-built ones so that
 * server-owned fields (like lastModified) are refreshed.
 */
export function moveDeletedFolderContentsToTarget(
  deletedFolderId: string,
  targetFolderId: string,
  response: MediaManagerAssetFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const targetFolder = findFolderInLibrary(targetFolderId, assetLibrary);
  if (targetFolderId !== 'ROOT' && !targetFolder) return false;
  const responseFoldersById = new Map((response.childFolders ?? []).map(folder => [folder.id, folder]));
  const responseAssetsById = new Map((response.assets ?? []).map(asset => [asset.getUniqueIdentifier(), asset]));
  const directChildFolders = filesystem.getDirectChildFolders(deletedFolderId);
  const currentFoldersById = new Map(
    directChildFolders.map(folder => [folder.id, findFolderInLibrary(folder.id, assetLibrary)])
  );
  const updatedFolders: MediaManagerFolderDto[] = [];
  for (const childFolder of directChildFolders) {
    const currentChildFolder = currentFoldersById.get(childFolder.id);
    if (!currentChildFolder) return false;
    updatedFolders.push(
      responseFoldersById.get(currentChildFolder.id) ?? buildUpdatedFolder(currentChildFolder, targetFolderId)
    );
  }
  if (!applyUpdatedFolders(currentFoldersById as Map<string, MediaManagerFolderDto>, updatedFolders, filesystem)) {
    return false;
  }
  const directChildAssets = filesystem.getDirectChildAssets(deletedFolderId);
  const currentAssetsById = new Map(
    directChildAssets.map(asset => [
      asset.getUniqueIdentifier(),
      findAssetInLibrary(asset.getUniqueIdentifier(), assetLibrary)
    ])
  );
  const updatedAssets: Asset[] = [];
  for (const childAsset of directChildAssets) {
    const canonicalChildAsset = currentAssetsById.get(childAsset.getUniqueIdentifier());
    if (!canonicalChildAsset) return false;
    updatedAssets.push(
      responseAssetsById.get(canonicalChildAsset.getUniqueIdentifier()) ??
        buildUpdatedAsset(canonicalChildAsset, targetFolderId)
    );
  }
  if (!applyUpdatedAssets(currentAssetsById as Map<string, Asset>, updatedAssets, filesystem)) {
    return false;
  }
  if (response.folder?.id === targetFolderId && targetFolder) {
    if (!patchFolderMetadata(targetFolderId, response.folder, filesystem)) return false;
    patchAssetLibraryFolder(targetFolder, response.folder);
  }
  return true;
}

/**
 * Remove a folder either wholesale (deleteAllItems) or by moving its contents to a target first. Returns false
 * when any step of the reconciliation would leave state inconsistent so the caller can refetch.
 */
export function applyDeletedFolder(
  deletedFolderId: string,
  targetFolderId: string,
  deleteAllItems: boolean,
  response: MediaManagerAssetFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const deletedFolder = findFolderInLibrary(deletedFolderId, assetLibrary);
  if (!deletedFolder) return false;
  if (deleteAllItems) {
    const deletedFolderIds = new Set([deletedFolderId, ...filesystem.getDescendantFolderIds(deletedFolderId)]);
    const deletedAssetIds = new Set(filesystem.getDescendantAssetIds(deletedFolderId));

    if (!filesystem.deleteFolder(deletedFolderId)) return false;

    removeDeletedFolderSubtreeFromAssetLibrary(deletedFolderIds, deletedAssetIds, assetLibrary);
    return true;
  }
  if (!moveDeletedFolderContentsToTarget(deletedFolderId, targetFolderId, response, filesystem, assetLibrary)) {
    return false;
  }
  if (!filesystem.deleteFolder(deletedFolderId)) return false;
  assetLibrary.assetFolders = assetLibrary.assetFolders.filter(folder => folder.id !== deletedFolderId);
  return true;
}
